import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { PermissionKey } from '@c4ytechnology/authz';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import { RoleEditorDialog } from '../components/admin/iam/RoleEditorDialog';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import { adminIamApi, type AdminIamPermission, type AdminIamRole } from '../api/adminConsole';
import { messageFromAdminConsoleAxiosError } from '../lib/axiosErrorMessage';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import { hasAdminPermission } from '../lib/adminPermissions';
import {
  ADMIN_IAM_PERMISSIONS_QUERY_KEY,
  ADMIN_IAM_ROLES_QUERY_KEY,
  adminIamRoleQueryKey,
  fallbackPermissionCatalog,
  groupRolePermissions,
  roleEditability,
  sortRoles,
} from '../lib/adminRolesView';
import { auditDeepLink } from '../lib/adminIamQuery';
import {
  assignmentStatusChip,
  effectiveAssignmentStatus,
  formatAssignmentExpiry,
} from '../lib/iamAssignmentStatus';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';

const RISK_COLOR: Record<AdminIamPermission['risk'], 'default' | 'warning' | 'error'> = {
  standard: 'default',
  sensitive: 'warning',
  critical: 'error',
};

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Roles (RBAC Phase 2): list, permissions, holders, and — with `iam.roles.write` — create, edit
 * (with a change preview) and delete. Super Admin is read-only; system roles cannot be deleted.
 */
export function AdminRolesPage() {
  const theme = useTheme();
  const qc = useQueryClient();
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const meQ = useAdminConsoleMe();
  const me = meQ.data;
  const canWrite = hasAdminPermission(me, PermissionKey.IAM_ROLES_WRITE);
  const canReadUsers = hasAdminPermission(me, PermissionKey.IAM_USERS_READ);
  const canReadAudit = hasAdminPermission(me, PermissionKey.ADMIN_AUDIT_READ);
  const [searchParams, setSearchParams] = useSearchParams();

  const rolesQ = useQuery({
    queryKey: ADMIN_IAM_ROLES_QUERY_KEY,
    queryFn: () => adminIamApi.roles(),
    enabled: hasToken,
  });
  const catalogQ = useQuery({
    queryKey: ADMIN_IAM_PERMISSIONS_QUERY_KEY,
    queryFn: () => adminIamApi.permissions(),
    enabled: hasToken,
    retry: false,
  });

  const roles = useMemo(() => sortRoles(rolesQ.data?.items ?? []), [rolesQ.data]);
  const selectedId = searchParams.get('role');
  const selected = roles.find((r) => r.id === selectedId) ?? roles[0] ?? null;
  const selectRole = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('role', id);
    setSearchParams(next, { replace: true });
  };

  const detailQ = useQuery({
    queryKey: adminIamRoleQueryKey(selected?.id ?? ''),
    queryFn: () => adminIamApi.role(selected!.id),
    enabled: hasToken && !!selected,
    retry: false,
  });

  const catalog = catalogQ.data?.items?.length ? catalogQ.data.items : fallbackPermissionCatalog();
  const sections = useMemo(
    () => (selected ? groupRolePermissions(selected.permissions, catalogQ.data?.items) : []),
    [selected, catalogQ.data],
  );
  const edit = selected ? roleEditability(me, selected) : null;

  const [editor, setEditor] = useState<{ role: AdminIamRole | null } | null>(null);
  const [deleting, setDeleting] = useState<AdminIamRole | null>(null);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ADMIN_IAM_ROLES_QUERY_KEY });
    await qc.invalidateQueries({ queryKey: ['adminIamRole'] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => adminIamApi.deleteRole(id),
    onSuccess: async () => {
      setDeleting(null);
      const next = new URLSearchParams(searchParams);
      next.delete('role');
      setSearchParams(next, { replace: true });
      await refresh();
    },
  });

  const headerEnd = useMemo(
    () => (
      <>
        <Chip label={`${roles.length} roles`} size="small" />
        {canWrite && (
          <Button variant="contained" size="small" onClick={() => setEditor({ role: null })}>
            New role
          </Button>
        )}
      </>
    ),
    [roles.length, canWrite],
  );

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to view roles.</Alert>
      </Box>
    );
  }

  if (rolesQ.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rolesQ.isError) {
    return (
      <Box sx={{ p: 4 }}>
        <IamErrorAlert error={rolesQ.error} />
      </Box>
    );
  }

  const holders = detailQ.data?.assignments ?? [];

  return (
    <Box>
      <AdminListPageHeader
        title="Roles"
        subtitle={
          canWrite
            ? 'Roles bundle permissions; staff get access through role assignments on the Users page.'
            : 'Roles bundle permissions; staff get access through role assignments on the Users page. Read-only.'
        }
        endAdornment={headerEnd}
      />

      {catalogQ.isError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Permission catalog unavailable ({messageFromAdminConsoleAxiosError(catalogQ.error)}); showing built-in labels.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 5fr) minmax(0, 7fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Card variant="outlined" sx={dataTableCardSx(theme)}>
          <Table size="small" stickyHeader sx={adminDataTableSx(theme)}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ width: 120 }}>Permissions</TableCell>
                <TableCell sx={{ width: 120 }}>Assigned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  selected={selected?.id === r.id}
                  onClick={() => selectRole(r.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {r.name}
                      </Typography>
                      {r.isSystem && <Chip label="System" size="small" variant="outlined" color="primary" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={r.permissions.length} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${r.assignmentCount} ${r.assignmentCount === 1 ? 'user' : 'users'}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      No roles defined.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        {selected && (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selected.name}
                  </Typography>
                  {selected.isSystem && <Chip label="System" size="small" variant="outlined" color="primary" />}
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>
                    {selected.key}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {canReadAudit && (
                    <Button component={RouterLink} to={auditDeepLink(selected.id, 'iam_role')} size="small">
                      Audit
                    </Button>
                  )}
                  {canWrite && edit && (
                    <>
                      <Tooltip title={edit.canEdit ? '' : edit.readOnlyReason ?? ''}>
                        <span>
                          <Button size="small" variant="outlined" disabled={!edit.canEdit} onClick={() => setEditor({ role: selected })}>
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={edit.canDelete ? '' : edit.deleteBlockedReason ?? ''}>
                        <span>
                          <Button
                            size="small"
                            color="error"
                            disabled={!edit.canDelete}
                            onClick={() => {
                              remove.reset();
                              setDeleting(selected);
                            }}
                          >
                            Delete
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Stack>
                {selected.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selected.description}
                  </Typography>
                )}
                {selected.key === 'super_admin' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Super Admin always holds every permission and is read-only.
                  </Typography>
                )}
                {sections.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    This role grants no permissions.
                  </Typography>
                ) : (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {sections.map((section) => (
                      <Box key={section.group}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                        >
                          {section.group}
                        </Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                          {section.items.map((item) => (
                            <Stack key={item.key} direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>
                                {item.key}
                              </Typography>
                              <Chip size="small" variant="outlined" color={RISK_COLOR[item.risk]} label={item.risk} />
                              {item.requiresMfa && <Chip size="small" variant="outlined" label="MFA" />}
                              {!item.known && <Chip size="small" variant="outlined" label="Not in catalog" />}
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Holders
                </Typography>
                {detailQ.isPending ? (
                  <CircularProgress size={22} />
                ) : detailQ.isError ? (
                  <IamErrorAlert error={detailQ.error} severity="warning" />
                ) : holders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nobody holds this role.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expiry</TableCell>
                        <TableCell>Granted by</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {holders.map((h) => {
                        const status = effectiveAssignmentStatus(h);
                        const chip = assignmentStatusChip(status);
                        const expired = status === 'expired';
                        return (
                          <TableRow key={h.id}>
                            <TableCell sx={expired ? { textDecoration: 'line-through', color: 'text.disabled' } : undefined}>
                              {canReadUsers ? (
                                <RouterLink to={`/admin/users/${encodeURIComponent(h.userId)}`}>
                                  {h.displayName || h.email}
                                </RouterLink>
                              ) : (
                                h.displayName || h.email
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: MONO }}>
                                {h.email}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAssignmentExpiry(h.expiresAt)}</TableCell>
                            <TableCell>{h.grantedBy ?? '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
      </Box>

      {editor && (
        <RoleEditorDialog
          open
          role={editor.role}
          catalog={catalog}
          actor={me}
          onClose={() => setEditor(null)}
          onSaved={async (saved) => {
            await refresh();
            if (saved?.id) selectRole(saved.id);
          }}
        />
      )}

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete role</DialogTitle>
        <DialogContent dividers>
          <IamErrorAlert error={remove.error} sx={{ mb: 2 }} />
          <Typography variant="body2">
            Delete the custom role <strong>{deleting?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!deleting || remove.isPending}
            onClick={() => deleting && remove.mutate(deleting.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
